import { useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { STATIC_SETTINGS } from "@/lib/api";
import { useAvailability } from "@/lib/useAvailability";
import { Reveal } from "@/components/Reveal";
import { BOOKING } from "@/constants/testIds";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

const fmtISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
};

const nightsBetween = (a, b) => Math.round((b - a) / 86400000);

export const Booking = ({ hideHeader = false }) => {
  const [range, setRange] = useState(undefined);
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: 2, message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const settings = STATIC_SETTINGS;
  const { status: availStatus, bookedDates, rangeHasBooked } = useAvailability();

  const disabledDays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [{ before: today }, ...bookedDates];
  }, [bookedDates]);

  const nights = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    return Math.max(0, nightsBetween(range.from, range.to));
  }, [range]);

  const total = useMemo(
    () => Math.round(nights * settings.nightly_rate * 100) / 100,
    [nights, settings.nightly_rate]
  );

  // Guard against a range that straddles a booked block. DayPicker's own
  // excludeDisabled handles most of it; this catches the rest and explains why.
  const handleSelect = (next) => {
    if (next?.from && next?.to && rangeHasBooked(next.from, next.to)) {
      toast.error("Those dates include nights that are already booked.");
      setRange({ from: next.from, to: undefined });
      return;
    }
    setRange(next);
  };

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to) {
      toast.error("Please select check-in and check-out dates.");
      return;
    }
    if (rangeHasBooked(range.from, range.to)) {
      toast.error("Those dates include nights that are already booked.");
      return;
    }
    if (nights < settings.min_nights) {
      toast.error(`Minimum stay is ${settings.min_nights} nights.`);
      return;
    }
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("form-name", "booking-request");
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("phone", form.phone);
      formData.append("guests", form.guests);
      formData.append("message", form.message);
      formData.append("check_in", fmtISO(range.from));
      formData.append("check_out", fmtISO(range.to));
      formData.append("nights", nights);
      formData.append("total", `${settings.currency} $${total.toLocaleString()}`);

      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });

      if (res.ok) {
        setConfirmation({ nights, total, currency: settings.currency, name: form.name });
        toast.success("Request received");
      } else {
        throw new Error(`Netlify Forms responded ${res.status}`);
      }
    } catch (err) {
      // Logged so a 404 here (form not detected at deploy time) is diagnosable
      // from the browser console rather than guessed at.
      console.error("Booking submission failed:", err);
      toast.error("Something went wrong. Please email us at bookings@atahouserarotonga.com");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <section id="book" className="py-24 md:py-32 bg-[hsl(var(--sage))]/15">
        <div className="container-editorial max-w-2xl text-center" data-testid={BOOKING.success}>
          <CheckCircle2 size={44} strokeWidth={1.2} className="mx-auto text-[hsl(var(--sage-deep))]" />
          <h2 className="editorial-heading mt-6 text-3xl md:text-4xl">Thanks — your request <em>has been received.</em></h2>
          <p className="mt-6 text-[hsl(var(--muted-foreground))]">
            AJ and Brad will personally check availability and reply within 24–48 hours.
            We&apos;ve made a note of your dates, your details, and any special requests.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-6 text-sm">
            <div className="border-t border-[hsl(var(--border))] pt-4">
              <div className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))]">Nights</div>
              <div className="font-display text-2xl mt-1">{confirmation.nights}</div>
            </div>
            <div className="border-t border-[hsl(var(--border))] pt-4">
              <div className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))]">Estimated total</div>
              <div className="font-display text-2xl mt-1">
                {confirmation.currency} ${confirmation.total.toLocaleString()}
              </div>
            </div>
          </div>
          <p className="mt-10 text-xs italic text-[hsl(var(--muted-foreground))]">
            Prefer to talk it through? Email AJ and Brad at{" "}
            <a className="underline" href="mailto:bookings@atahouserarotonga.com">
              bookings@atahouserarotonga.com
            </a>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="book" data-testid={BOOKING.section} className={hideHeader ? "py-12 md:py-20" : "py-24 md:py-32"}>
      <div className="container-editorial">
        {!hideHeader && (
          <Reveal className="max-w-2xl mb-14 md:mb-20">
            <div className="eyebrow-rule">Book your stay</div>
            <h2 className="editorial-heading text-3xl md:text-4xl lg:text-[52px]">
              Send AJ &amp; Brad <em>a request.</em>
            </h2>
            <p className="mt-6 text-[hsl(var(--muted-foreground))] max-w-xl">
              Ata House is request-to-book. Choose your dates, share a few details, and
              we&apos;ll personally confirm within 24–48 hours.
            </p>
          </Reveal>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-12">
          {/* Calendar */}
          <Reveal className="lg:col-span-8">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="inline-block px-3 py-1 rounded-full bg-[hsl(var(--sage))]/35 text-[hsl(var(--ink))] text-[10px] tracking-[0.28em] uppercase">
                  Min. {settings.min_nights} nights
                </span>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">
                  {settings.currency} ${settings.nightly_rate}/night
                </span>
              </div>
            </div>
            <div
              className="ata-calendar bg-[hsl(var(--card))] rounded-2xl p-4 md:p-8 border border-[hsl(var(--border))]"
              data-avail={availStatus}
            >
              <DayPicker
                mode="range"
                numberOfMonths={typeof window !== "undefined" && window.innerWidth >= 1024 ? 2 : 1}
                selected={range}
                onSelect={handleSelect}
                disabled={disabledDays}
                excludeDisabled
                modifiers={{ booked: bookedDates }}
                modifiersClassNames={{ booked: "ata-day-booked" }}
                data-testid={BOOKING.calendar}
              />
              <div className="mt-6 flex flex-wrap gap-5 text-xs text-[hsl(var(--muted-foreground))]">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 rounded-full bg-[hsl(var(--sage-deep))]" />
                  Selected
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 rounded-full bg-[hsl(var(--card))] border border-[hsl(var(--border))]" />
                  Available
                </div>
                <div className="flex items-center gap-2">
                  <span className="ata-legend-booked inline-block w-3 h-3 rounded-full" />
                  Booked
                </div>
                <button
                  type="button"
                  onClick={() => setRange(undefined)}
                  data-testid={BOOKING.clearDates}
                  className="ml-auto underline underline-offset-4 hover:text-[hsl(var(--ink))]"
                >
                  Clear dates
                </button>
              </div>
              {availStatus === "error" && (
                <p className="mt-4 text-xs italic text-[hsl(var(--muted-foreground))]">
                  We couldn&apos;t load live availability just now — please send your dates
                  through and we&apos;ll confirm them personally.
                </p>
              )}
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={120} className="lg:col-span-4">
            <form
              name="booking-request"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={submit}
              className="space-y-5"
            >
              <input type="hidden" name="form-name" value="booking-request" />
              <p className="hidden"><input name="bot-field" /></p>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl bg-[hsl(var(--marble))] p-5">
                  <div className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))] mb-1">
                    Check-in
                  </div>
                  <div data-testid={BOOKING.checkin} className="font-display text-lg">
                    {range?.from ? range.from.toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                  </div>
                </div>
                <div className="rounded-2xl bg-[hsl(var(--marble))] p-5">
                  <div className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))] mb-1">
                    Check-out
                  </div>
                  <div data-testid={BOOKING.checkout} className="font-display text-lg">
                    {range?.to ? range.to.toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                  </div>
                </div>
              </div>

              <Field label="Full name">
                <input required name="name" data-testid={BOOKING.name} value={form.name} onChange={update("name")} className="ata-input" placeholder="Your name" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Email">
                  <input required type="email" name="email" data-testid={BOOKING.email} value={form.email} onChange={update("email")} className="ata-input" placeholder="you@example.com" />
                </Field>
                <Field label="Phone">
                  <input required name="phone" data-testid={BOOKING.phone} value={form.phone} onChange={update("phone")} className="ata-input" placeholder="+64 ..." />
                </Field>
              </div>
              <Field label="Number of guests">
                <input type="number" min={1} max={12} name="guests" data-testid={BOOKING.guests} value={form.guests} onChange={update("guests")} className="ata-input" />
              </Field>
              <Field label="Anything we should know?">
                <textarea rows={4} name="message" data-testid={BOOKING.message} value={form.message} onChange={update("message")} className="ata-input" placeholder="Special requests, occasion, arrival flight..." />
              </Field>

              <div data-testid={BOOKING.total} className="rounded-2xl bg-[hsl(var(--oak))]/25 p-6">
                <div className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))]">Estimated total</div>
                <div className="font-display text-3xl mt-1 whitespace-nowrap">{settings.currency} ${total.toLocaleString()}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  {nights || 0} {nights === 1 ? "night" : "nights"} · ${settings.nightly_rate}/night
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={BOOKING.submit}
                  className="btn-pill btn-dark w-full mt-5 whitespace-nowrap"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : "Request to Book"}
                </button>
              </div>

              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Bookings are request-to-book — no payment is taken on this site.
                Availability syncs from our calendar and can take a few hours to update,
                so we&apos;ll always confirm your dates before anything is finalised.
                Prefer to talk it through? Contact AJ and Brad at{" "}
                <a className="underline" href="mailto:bookings@atahouserarotonga.com">bookings@atahouserarotonga.com</a>.
              </p>
            </form>
          </Reveal>
        </div>
      </div>

      <style>{`
        .ata-input { width: 100%; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); border-radius: 0.875rem; padding: 0.85rem 1rem; font-size: 0.95rem; color: hsl(var(--ink)); transition: border-color 0.3s ease; }
        .ata-input:focus { outline: none; border-color: hsl(var(--sage-deep)); }
        .ata-input::placeholder { color: hsl(var(--muted-foreground)); }

        /* Dim the grid while availability is still loading so guests don't
           pick a date that is about to become unavailable. */
        .ata-calendar[data-avail="loading"] .rdp { opacity: 0.45; pointer-events: none; transition: opacity 0.25s ease; }

        /* Centre the two month grids so any spare width in the card is shared
           evenly rather than pooling on the left. */
        .ata-calendar .rdp-months { justify-content: center; }

        /* Booked nights read as filled rather than merely faded, so a guest can
           tell an already-taken week from a date that has simply passed.
           The transparent border plus padding-box clipping shrinks the visible
           circle inside its cell, so consecutive booked days keep a gap
           between them instead of merging into one solid band. */
        .ata-calendar .rdp-day.ata-day-booked {
          border: 4px solid transparent;
          background-clip: padding-box;
          background-color: hsl(var(--oak) / 0.45);
          color: hsl(var(--ink));
          opacity: 1;
          text-decoration: line-through;
          text-decoration-thickness: 1px;
          text-decoration-color: hsl(var(--oak-deep));
        }
        .ata-legend-booked { background: hsl(var(--oak) / 0.45); }

        @media (prefers-reduced-motion: reduce) {
          .ata-calendar[data-avail="loading"] .rdp { transition: none; }
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))] block mb-2">{label}</span>
    {children}
  </label>
);
